import React from 'react';
import { motion } from 'framer-motion';

interface PricingOption {
  title: string;
  features: string[];
  highlighted?: boolean;
  isEnterprise?: boolean;
  ctaText?: string;
  bookingLink?: string;
}

interface PricingSectionProps {
  title: string;
  options: PricingOption[];
}

const PricingSection: React.FC<PricingSectionProps> = ({ title, options }) => {
  const handleBookCall = (bookingLink?: string) => {
    const defaultBookingLink = "https://cal.com/kloudskool/30min"; // Updated to Cal.com link
    window.location.href = bookingLink || defaultBookingLink;
  };

  return (
    <section className="py-20 bg-gradient-to-br from-[#F8FAFC] via-white to-[#F0F9FF]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-[#15213A] mb-4">{title}</h2>
          <p className="text-gray-600 max-w-3xl mx-auto mb-8 text-lg">
            Schedule a no-obligation consultation to discuss your cloud career goals and discover if the Cloud Acceleration Program is right for you.
          </p>
        </div>

        {/* Single Card - Centered and Larger */}
        <div className="max-w-3xl mx-auto">
          {options.map((option, index) => (
            <motion.div
              key={option.title}
              className="relative rounded-2xl overflow-hidden shadow-xl bg-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ 
                y: -4,
                boxShadow: "0 25px 30px -5px rgba(0, 0, 0, 0.15), 0 15px 15px -5px rgba(0, 0, 0, 0.08)",
                transition: { duration: 0.2, ease: "easeOut" }
              }}
            >
              <div className="absolute top-0 right-0 bg-[#F97316] text-white px-4 py-1 rounded-bl-lg text-sm font-medium z-10">
                Free Consultation
              </div>
              <div className="p-10 h-full flex flex-col">
                <div>
                  <h3 className="text-3xl font-bold mb-6 text-[#15213A] text-center">{option.title}</h3>
                  
                  <div className="bg-gray-50 p-6 rounded-lg mb-8">
                    <h4 className="text-[#15213A] text-xl font-semibold mb-4">During this call we will:</h4>
                    <ul className="space-y-4 mb-6">
                      {option.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <svg
                            className="h-6 w-6 flex-shrink-0 text-[#F97316]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="ml-3 text-gray-700 text-lg">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-8 text-gray-700 space-y-4">
                    <p className="flex items-center">
                      <svg className="h-5 w-5 mr-3 text-[#F97316]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Duration: 30 minutes
                    </p>
                    <p className="flex items-center">
                      <svg className="h-5 w-5 mr-3 text-[#F97316]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Via Zoom or Phone
                    </p>
                    <p className="flex items-center">
                      <svg className="h-5 w-5 mr-3 text-[#F97316]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      No obligation, completely free
                    </p>
                  </div>
                </div>
                <div className="mt-auto">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-4 px-6 rounded-lg font-bold transition-colors duration-200 text-xl bg-[#F97316] text-white hover:bg-[#F97316]/90"
                    onClick={() => handleBookCall(option.bookingLink)}
                  >
                    {option.ctaText || 'Book Your Free Call Now'}
                  </motion.button>
                  
                  <p className="text-gray-500 text-center mt-4 text-sm">
                    Limited spots available each week. Schedule now to secure your slot.
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;